import {
  BankStatementAction,
  BankStatementOpenAmountType,
  type BankStatementEntryDto,
  type BankStatementEntryPageDto,
  type BankStatementPaymentTargetDto,
  type BankStatementListStatus,
  type BankStatementOpenAmountDto,
  type ConfirmBankStatementActionDto,
  type BankStatementIngestResultDto,
  type BankStatementWebhookPayloadDto,
} from '@carvia/ros-client-types';
import { Service } from './http-service';

export function getActionForOpenAmountType(type: BankStatementOpenAmountType): BankStatementAction {
  if (type === BankStatementOpenAmountType.PAYMENT) return BankStatementAction.LINK_PAYMENT;
  if (type === BankStatementOpenAmountType.SETTLEMENT) return BankStatementAction.LINK_SETTLEMENT;
  return BankStatementAction.CREATE_PAYMENT;
}

export function toPaymentTarget(
  target: Pick<BankStatementOpenAmountDto, 'invoice' | 'booking' | 'counterpartyType'>,
): BankStatementPaymentTargetDto {
  if (target.invoice) return { invoiceId: target.invoice.id };
  return {
    bookingId: target.booking?.id,
    counterpartyType: target.counterpartyType ?? undefined,
  };
}

/** The same open amount, or an invoice remainder and the booking remaining to pay covering it. */
export function doOpenAmountsOverlap(
  a: Pick<BankStatementOpenAmountDto, 'key' | 'coveredByKey'>,
  b: Pick<BankStatementOpenAmountDto, 'key' | 'coveredByKey'>,
): boolean {
  return a.key === b.key || a.coveredByKey === b.key || b.coveredByKey === a.key;
}

/** Sum of open amounts in euros, added in cents. */
export function sumOpenAmounts(items: Array<Pick<BankStatementOpenAmountDto, 'amount'>>): number {
  return Math.round(items.reduce((sum, item) => sum + Math.round(item.amount * 100), 0)) / 100;
}

/** Entry amount minus the chosen open amounts, in euros; positive means money left over. */
export function getAmountDifference(entryAmount: number, sum: number): number {
  return (Math.round(entryAmount * 100) - Math.round(sum * 100)) / 100;
}

class BankStatementsService extends Service {
  async listAccounts() {
    const res = await this.httpClient.get<string[]>(`${this.endpoint}/accounts`);
    return res.data;
  }

  async getEntries(params: {
    cursor?: string;
    limit: number;
    status?: BankStatementListStatus;
    accountIban?: string;
    legalEntityId?: string;
    startDate?: string;
    endDate?: string;
  }) {
    const res = await this.httpClient.get<BankStatementEntryPageDto>(this.endpoint, {
      params: {
        ...params,
        accountIban: params.accountIban || undefined,
      },
    });
    return res.data;
  }

  async getOpenAmounts(entryId: string, q?: string) {
    const res = await this.httpClient.get<BankStatementOpenAmountDto[]>(
      `${this.endpoint}/${entryId}/open-amounts`,
      { params: { q: q || undefined, limit: 50 } },
    );
    return res.data;
  }

  /** Open invoices and bookings one payer owes, oldest due first. */
  async getPayerOpenAmounts(entryId: string, payerUserId: string) {
    const res = await this.httpClient.get<BankStatementOpenAmountDto[]>(
      `${this.endpoint}/${entryId}/open-amounts`,
      { params: { payerUserId } },
    );
    return res.data;
  }

  /** The same ingest the bank sync posts to; used for hand-made test entries. */
  async ingest(payload: BankStatementWebhookPayloadDto) {
    const res = await this.httpClient.post<BankStatementIngestResultDto>(
      `${this.endpoint}/webhook`,
      payload,
    );
    return res.data;
  }

  async confirm(entryId: string, body: ConfirmBankStatementActionDto) {
    const res = await this.httpClient.post<BankStatementEntryDto>(
      `${this.endpoint}/${entryId}/confirm`,
      body,
    );
    return res.data;
  }
}

export const bankStatementsService = new BankStatementsService('/bank-statements');
